import { useEffect, useState } from "react"
import {
  Lightbulb,
  Target,
  Compass,
  Rocket,
  ClipboardList,
  Layers,
  CheckCircle2,
  Globe
} from "lucide-react"
import { useLanguage } from "./LanguageContext"
import { loadAppConfiguration } from "./ContentLoader"
import { useSeo } from "./hooks/useSeo"
import Navigation, { Footer } from "./Navigation"
import LessonLauncher from "./LessonLauncher"
import "./css/AboutPage.css"
import "./css/GameSelector.css"
import "./css/TeachersPage.css"


type TeachersPageProps = {
  onReturnHome?: () => void
}

const iconMap: Record<string, any> = {
  lightbulb: Lightbulb,
  target: Target,
  compass: Compass,
  rocket: Rocket,
}

const LUCIDE_ICON_MAP: Record<string, React.FC<any>> = {
  ClipboardList,
  Layers,
  Globe,
  Lightbulb,
  CheckCircle2
}


// Fallback copy in case content JSON is missing the one-liner for SLO cards
const dlSubtextFallback: Record<string, { nl: string; en: string }> = {
  "Praktische kennis & vaardigheden": {
    nl: "Concrete vaardigheden die direct toepasbaar zijn — van didactische technieken tot probleemoplossend werken en samenwerken in de praktijk.",
    en: "Concrete skills you can use right away — from practical routines to problem-solving and collaboration.",
  },
  "Ontwerpen & maken": {
    nl: "Creatief denken omzetten in tastbare oplossingen — ideeën ontwerpen, testen, verbeteren en realiseren met digitale én fysieke middelen.",
    en: "Turn ideas into tangible solutions — design, test, improve, and build with digital and physical tools.",
  },
  "De gedigitaliseerde wereld": {
    nl: "Inzicht in technologie en media — hoe systemen werken, hoe je ze kritisch en verantwoord gebruikt, en wat de impact is op de samenleving.",
    en: "Understand tech and media — how systems work, how to use them responsibly, and their impact on society.",
  },
}

const TeachersPage = ({ onReturnHome }: TeachersPageProps) => {
  const { language } = useLanguage()

  const [appConfig, setAppConfig] = useState<any>(null)
  const [content, setContent] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(true)

  // ---- Video modal state ----
  const [isVideoOpen, setIsVideoOpen] = useState(false)

  // Prevent background scroll when modal is open
  useEffect(() => {
    if (!isVideoOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = prev
    }
  }, [isVideoOpen])

  // Close on ESC
  useEffect(() => {
    if (!isVideoOpen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsVideoOpen(false)
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [isVideoOpen])

  // Same helper pattern as About/Landing
  const t = (path: string, defaultText = "") => {
    if (!appConfig) return defaultText
    const keys = path.split(".")
    let value: any = appConfig
    for (const key of keys) {
      if (!value || !value[key]) return defaultText
      value = value[key]
    }
    if (value && typeof value === "object" && value[language.code]) return value[language.code]
    return typeof value === "string" ? value : value?.en || defaultText
  }

  const L = (obj: any, fallback = "") => {
    if (!obj) return fallback
    return obj?.[language.code] || obj?.en || fallback
  }

  useSeo({
    path: "/teachers",
    title:
      language.code === "en"
        ? "Techtok for teachers: digital literacy for your class"
        : "Techtok voor leerkrachten: digitale geletterdheid voor je klas",
    description:
      language.code === "en"
        ? "Run playful digital-literacy lessons with no prep. See how Techtok maps to the Dutch SLO curriculum for primary schools."
        : "Geef speelse lessen digitale geletterdheid zonder voorbereiding. Bekijk hoe Techtok aansluit op de SLO-kerndoelen voor het basisonderwijs.",
  })

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      try {
        const companyId = new URLSearchParams(window.location.search).get("company") || null
        const config = await loadAppConfiguration(language.code, companyId, false)
        setAppConfig(config)

        if (config?.branding?.accentColor) {
          document.documentElement.style.setProperty("--accent-color", config.branding.accentColor)
        }

        const res = await fetch(`/content/main/app-teachers.json`)
        if (!res.ok) throw new Error(`Failed to load teacher content: ${res.status}`)
        const data = await res.json()
        setContent(data)
      } catch (e) {
        console.error("Failed to load TeachersPage:", e)
      } finally {
        setIsLoading(false)
      }
    }
    fetchData()
  }, [language.code])

  const handleNavigation = (page: string) => {
    if (page === "home") onReturnHome?.()
  }

  const getIcon = (iconKey?: string) => {
    const Icon = (iconKey && iconMap[iconKey]) || Lightbulb
    return <Icon />
  }

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    )
  }

  const heroImg = content?.hero?.image

  const videoTitle = L(content?.video?.title, "")
  const videoSubtitle = L(content?.video?.subtitle, "")
  const videoButtonLabel = L(content?.video?.buttonLabel, "")
  const videoTeaserMp4 = content?.video?.teaserMp4 || ""
  const videoYoutubeEmbed = content?.video?.youtubeEmbed || ""
  const showVideo = Boolean(videoYoutubeEmbed)

  // The lesson grid lives here, not on the home page (#415): the home page
  // sells Missies to parents, the classroom lessons are for teachers.
  // LessonLauncher hands us the grid; opening a lesson shows its overview
  // inside the normal header/footer instead of the rest of this page.
  return (
  <LessonLauncher
    languageCode={language.code}
    beta={false}
    variant="landing"
    limit={6}
    enableDeepLink={false}
    renderShell={(children, mode) => {
      if (mode !== "grid") {
        return (
          <div className="landing-selector-container">
            <Navigation appConfig={appConfig} currentPage="teachers" onNavigate={handleNavigation} t={t} />
            <main className="main-content">{children}</main>
            <Footer appConfig={appConfig} t={t} />
          </div>
        )
      }

      return (
    <div className="landing-selector-container">
      {/* IMPORTANT: reuse Navigation exactly like Landing/About */}
      <Navigation appConfig={appConfig} currentPage="teachers" onNavigate={handleNavigation} t={t} />

      {/* HERO (EXACT STRUCTURE from LandingPage.tsx) */}
      <div className="hero-section-top relative overflow-hidden">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 items-center px-[40px] py-[0px]">
            <div className="text-left">
             

              <h1 className="hero-title">{L(content?.hero?.title, "Voor leerkrachten")}</h1>
              <p className="hero-subtitle">{L(content?.hero?.subtitle, "")}</p>

              <div className="page" style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginTop: "32px" }}>
                <a className="main-action-button" href={content?.hero?.ctaPrimary?.href || "/lessons"}>
                  {L(content?.hero?.ctaPrimary?.label, "Start met lessen")}
                </a>

                <a className="secondary-button" href={content?.hero?.ctaSecondary?.href || "#standards"}>
                  {L(content?.hero?.ctaSecondary?.label, "Bekijk kerndoelen & SLO")}
                </a>
              </div>
            </div>

            {heroImg && (
              <img
                src={heroImg}
                alt="Voor leerkrachten"
                className="mx-auto mb-6 w-full max-w-3xl rounded-2xl"
                style={{ objectFit: "cover" }}
              />
            )}
          </div>
        </div>

        {/* Playful Wave Divider (EXACT from LandingPage) */}
        {/* Playful Wave Divider */}
          <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none">
            <svg className="relative" viewBox="0 0 1200 120" preserveAspectRatio="none">
              {/* Fun floating dots */}
              <circle cx="200" cy="40" r="8" fill="#FAFF05" opacity="0.8">
                <animate attributeName="cy" values="30;50;30" dur="3s" repeatCount="indefinite" />
              </circle>
              <circle cx="900" cy="30" r="6" fill="#8CFFCF" opacity="0.7">
                <animate attributeName="cy" values="20;40;20" dur="2.5s" repeatCount="indefinite" />
              </circle>

            </svg>
          </div>
      </div>

      {/* FORMATS: Missies (self-paced game) vs classroom lessons, same content */}
      {content?.formats && (
        <section className="about-main">
          <div className="hero-content">
            <div className="hero-content-inner">
              <h1 className="hero-title">{L(content.formats.title, "Twee vormen, dezelfde inhoud")}</h1>
            </div>
          </div>
          <div className="about-sections formats-sections">
            {content.formats.cards?.map((card: any, idx: number) => {
              const IconComp = LUCIDE_ICON_MAP[card.icon] || iconMap[card.icon] || Lightbulb
              return (
                <section key={idx} className="about-section">
                  <div className="section-icon">
                    <IconComp size={22} />
                  </div>
                  <h2 className="section-heading">{L(card.title, "")}</h2>
                  <p className="section-content-page">{L(card.text, "")}</p>
                </section>
              )
            })}
          </div>
        </section>
      )}

      {/* LESSONS: the "Kies je eigen leeravontuur" grid, moved here from the home page (#415) */}
      <section className="games-section" id="lessen">
        <div className="hero-content">
          <div className="hero-content-inner">
            <h1 className="hero-title">{L(content?.lessons?.title, t("heroSection.lessonHeader", "Kies je eigen leeravontuur"))}</h1>
            <p className="hero-subtitle">{L(content?.lessons?.subtitle, "")}</p>
          </div>
        </div>
        {children}
      </section>

      {/* VIDEO (moved here from LandingPage.tsx — general Techtok lesson demo, not game footage) */}
      {(videoTitle || videoSubtitle) && (
        <section className="hero-section">
          <div className="hero-content">
            <div className="hero-content-inner">
              {videoTitle && <h1 className="hero-title">{videoTitle}</h1>}
              {videoSubtitle && <p className="hero-subtitle">{videoSubtitle}</p>}
            </div>
          </div>
        </section>
      )}

      {showVideo && (
        <section className="video-teaser-section">
          <div className="video-teaser-inner">
            <div
              className="video-teaser-frame video-teaser-clickable"
              role="button"
              tabIndex={0}
              aria-label="Open video"
              onClick={() => setIsVideoOpen(true)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") setIsVideoOpen(true)
              }}
            >
              {videoTeaserMp4 ? (
                <video className="video-teaser-preview" autoPlay muted loop playsInline src={videoTeaserMp4} />
              ) : (
                <iframe
                  className="video-teaser-preview"
                  src="https://www.youtube-nocookie.com/embed/pFULQIm2FZI?autoplay=1&mute=1&controls=0&playsinline=1&loop=1&playlist=pFULQIm2FZI&modestbranding=1&rel=0"
                  title="Techtok video preview"
                  frameBorder="0"
                  allow="autoplay; encrypted-media; picture-in-picture"
                  referrerPolicy="strict-origin-when-cross-origin"
                />
              )}

              <div className="video-teaser-overlay">
                <button
                  type="button"
                  className="main-action-button video-teaser-button"
                  onClick={(e) => {
                    e.stopPropagation()
                    setIsVideoOpen(true)
                  }}
                >
                  {videoButtonLabel || "Bekijk de video"}
                </button>
              </div>
            </div>
          </div>

          {isVideoOpen && (
            <div
              className="video-modal-backdrop"
              role="dialog"
              aria-modal="true"
              aria-label="Techtok video player"
              onClick={() => setIsVideoOpen(false)}
            >
              <div className="video-modal" onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  className="video-modal-close"
                  onClick={() => setIsVideoOpen(false)}
                  aria-label="Close video"
                >
                  ✕
                </button>

                <div className="video-modal-player">
                  <iframe
                    className="video-modal-iframe"
                    src={videoYoutubeEmbed}
                    title="Techtok video"
                    frameBorder="0"
                    allow="autoplay; encrypted-media; picture-in-picture"
                    allowFullScreen
                    referrerPolicy="strict-origin-when-cross-origin"
                  />
                </div>
              </div>
            </div>
          )}
        </section>
      )}

      {/* WHY (reuse About cards grid exactly) */}
   {/* WHY (fixed: title is NOT inside the about-sections grid) */}
<section className="about-main">
  <div className="hero-content">
    <div className="hero-content-inner">
      <h1 className="hero-title">{L(content?.why?.title, "Waarom Techtok in de klas?")}</h1>
    </div>
  </div>

<div className="about-sections">
  {content?.why?.cards?.map((card: any, idx: number) => {
    // Supports BOTH:
    // 1) JSON icon like "Layers" (Lucide component name)
    // 2) JSON icon like "lightbulb" / "target" (your existing iconMap keys)
    const iconKey = card?.icon
    const IconComp =
      iconKey
        ? (LUCIDE_ICON_MAP[iconKey] ||
           iconMap[iconKey] ||
           iconMap[String(iconKey).toLowerCase()])
        : undefined

    return (
      <section key={idx} className="about-section">
        <div className="section-icon">
          {card.image ? (
            <img
              src={card.image}
              alt=""
              style={{ width: 24, height: 24, objectFit: "contain", display: "block" }}
            />
          ) : IconComp ? (
            <IconComp size={22} />
          ) : (
            <Lightbulb size={22} />
          )}
        </div>

        <h2 className="section-heading">{L(card.title, "")}</h2>
        <p className="section-content-page">{L(card.text, "")}</p>
      </section>
    )
  })}
</div>


</section>


      {/* HOW TO USE (EXACT “motivation-section” wave block from Landing/About) */}
      <div className="motivation-section">
        <section className="hero-section">
          <div className="hero-content">
            <div className="hero-content-inner">
              <h1 className="hero-title">{L(content?.howToUse?.title, "Zo gebruik je Techtok")}</h1>
            </div>
          </div>
        </section>

        <section className="about-main">
          <div className="about-sections">
            {content?.howToUse?.steps?.map((s: any, idx: number) => (
              <section key={idx} className="about-section">
                <h2 className="section-heading">{L(s.title, "")}</h2>
                <p className="section-content-page" style={{ fontWeight: 800, color: "var(--color-primary)" }}>
                  {L(s.time, "")}
                </p>
                <ul className="section-content-page">
                  {(s.bullets?.[language.code] || s.bullets?.en || []).map((b: string, i: number) => (
                    <li key={i} style={{ marginBottom: "6px" }}>
                      {b}
                    </li>
                  ))}
                </ul>
              </section>
            ))}

           
          </div>
        </section>
      </div>

      {/* CLASSROOM NEEDS (EXACT “team-main” wave block from About) */}
      <div className="team-main">
        <section className="hero-section">
          <div className="hero-content">
            <div className="hero-content-inner">
              <h1 className="hero-title">{L(content?.classroom?.title, "Wat heb je nodig?")}</h1>
            </div>
          </div>
        </section>

        <section className="about-main">
          <div className="about-sections">
            {content?.classroom?.blocks?.map((b: any, idx: number) => (
              <section key={idx} className="about-section">
                <h2 className="section-heading">{L(b.title, "")}</h2>
                <p className="section-content-page">{L(b.text, "")}</p>
              </section>
            ))}
          </div>
        </section>
      </div>

      {/* STANDARDS (EXACT “ai-tools-section” wave block from About/Landing) */}
    {/* STANDARDS (Digital literacy first, kerndoelen secondary) */}
<div className="ai-tools-section" id="standards">
  <section className="hero-section">
    <div className="hero-content">
      <div className="hero-content-inner">
        <h1 className="hero-title">{L(content?.standards?.title, "Digitale geletterdheid & kerndoelen")}</h1>
        <p className="hero-subtitle">{L(content?.standards?.intro, "")}</p>
      </div>
    </div>
  </section>

  <section className="about-main tt-standards">
    {/* 3 big digital literacy cards */}


<div className="ai-tools-grid">
  {(content?.standards?.digitalLiteracy?.cards || []).map((c: any, idx: number) => {
    const title = L(c.title, "")
    const description =
      L(c.oneLiner || c.text, "") ||
      dlSubtextFallback[title]?.[language.code] ||
      dlSubtextFallback[title]?.en ||
      ""

    const detailsTitle = L(c.detailsTitle || c.howTitle, language.code === "nl" ? "Hoe Techtok dit dekt" : "How Techtok covers this")
    const detailsLines =
      ((c.details?.[language.code] || c.details?.en || c.how?.[language.code] || c.how?.en || []) as string[])

    const Icon = idx === 0 ? ClipboardList : idx === 1 ? Layers : CheckCircle2
    const badgeColorClass =
      idx === 1 ? "ai-tool-badge-secondary" : idx === 2 ? "ai-tool-badge-tertiary" : ""

    const badgeText =
      idx === 0 ? (language.code === "nl" ? "VAARDIGHEDEN" : "SKILLS") :
      idx === 1 ? (language.code === "nl" ? "MAKEN" : "MAKE") :
                  (language.code === "nl" ? "IMPACT" : "IMPACT")



    return (
      <details key={idx} className="ai-tool-card">
        <summary style={{ listStyle: "none", cursor: "pointer" as const }}>
          <div className="ai-tool-card-header">
          


          </div>

          <h3 className="ai-tool-card-title">{title}</h3>
          <p className="ai-tool-card-description">{description}</p>

        <div className="ai-tool-card-footer ai-tool-card-toggle">
  <span className="ai-tool-link-text ai-tool-toggle-text">
    <span className="ai-tool-toggle-closed">{language.code === "nl" ? "Meer info" : "More info"}</span>
    <span className="ai-tool-toggle-open">{language.code === "nl" ? "Sluiten" : "Close"}</span>
  </span>
  <span className="ai-tool-toggle-chevron" aria-hidden="true" />
</div>

        </summary>

      <div className="ai-tool-details">
  <div className="ai-tool-details-divider" />

  <div className="ai-tool-details-title">
    {detailsTitle}
  </div>

  <ul className="ai-tool-details-list">
    {detailsLines.map((line, i) => (
      <li key={i}>{line}</li>
    ))}
  </ul>
</div>

      </details>
    )
  })}
</div>



  


  </section>
</div>


      {/* FAQ (reuse “team-main” wave block style for the light-blue band) */}
      <div className="team-main">
        <section className="hero-section">
          <div className="hero-content">
            <div className="hero-content-inner">
              <h1 className="hero-title">{L(content?.faq?.title, "Veelgestelde vragen")}</h1>
            </div>
          </div>
        </section>

        <section className="about-main">
          <div className="about-sections">
            {content?.faq?.items?.map((item: any, idx: number) => (
              <section key={idx} className="about-section" style={{ gridColumn: "1 / -1" }}>
                <details>
                  <summary className="section-heading" style={{ cursor: "pointer" }}>
                    {L(item.q, "")}
                  </summary>
                  <p className="section-content-page" style={{ marginTop: "10px" }}>
                    {L(item.a, "")}
                  </p>
                </details>
              </section>
            ))}
          </div>
        </section>
      </div>

      {/* EXIT BANNER (EXACT same dark block style as Landing/About) */}
      <div className="exit-banner">
        <div className="coming-soon-section">
          <h1 className="hero-title">{L(content?.cta?.title, "Klaar om te starten?")}</h1>
          <p className="hero-subtitle">{L(content?.cta?.text, "")}</p>

         <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap", marginTop: "18px" }}>
  <a className="main-action-button" href={content?.cta?.primary?.href || "/lessons"}>
    {L(content?.cta?.primary?.label, "Ga naar lessen")}
  </a>
</div>

        </div>
      </div>

      <Footer appConfig={appConfig} t={t} />
    </div>
      )
    }}
  />
  )
}

export default TeachersPage
